// Split out of src/components/Wizard.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Wizard.jsx
// re-exports everything it exported before, so existing imports keep working.

import { DailyHabitsSection } from "../AdjustSections.jsx";
import { CoinIcon, Icon, SafeAreaSpacer, ChildMark } from "../Common.jsx";
import { LE_GUIDE_STEPS, LearningAdminTab } from "../LearningAdminTab.jsx";
import { CURRENCY_SYMBOL, DEFAULT_SPENDING_CAP_GBP } from "../../lib/constants.js";
import { fmtPence, spendingCapPence } from "../../lib/utils.js";
import { ChildTasksSection } from "../ChildTasksSection.jsx";
import { EarningsHealthLine } from "../EarningsHealthLine.jsx";
import { defaultSeedSourceFor, sameBandSiblings, seedChildFromSource } from "../../lib/spendingDefaults.js";
import { isYoungChild } from "../../lib/age.js";
import { t as ct } from "../../lib/content.js";
import { track } from "../../lib/metrics.js";
import { CAP_PRESETS, WizardIntro, WizardFooter, WizardBackButton } from "./wizardShared.jsx";
import { SetupFork, SetupReviewStep } from "./steps.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* Adding a child later (LB-129, amended 2 Sep; LB-127 PR4).
 *
 * The same primary/secondary pattern as setup, in a sheet over Parent Mode, for a child
 * added from the Kids tab. "Set it up for me" is primary; behind it, auto copies a
 * same-band sibling when one exists (linked, cap-sized — everything LB-129 decided) and
 * seeds the standard set for the band when none does. The review shows where the setup
 * came from — "Based on Maya's setup" / "Standard set for ages 8 and over" — with a Change
 * link to pick a different sibling or the standard set. "Take me through it" is the guided
 * walk for this one child: habits, tasks, then the six Learn & Earn parts.
 *
 * Copy is preserved exactly as decided; it stops being a decision the parent makes on a
 * screen with no context. Same review component as setup, same Change-and-return, same
 * cap edit, same seeding rules (untouched rows only).
 */
export function NewChildSetupSheet({ child, state, update, showToast, onDone }) {
  // cap → fork → review | habits → tasks → le. The cap comes FIRST (Alan, 4 Sep): it is
  // the one number every other amount derives from, and the add-child form never asked
  // for it — a child added later got the household default and the parent only met the
  // figure at the foot of a screen they could not change it on.
  const [stage, setStage] = useState("cap");
  const [fromReview, setFromReview] = useState(false);
  const [lePart, setLePart] = useState(0);
  const [pickingSource, setPickingSource] = useState(false);
  const [step4Open, setStep4Open] = useState({ habits: true, activities: false });
  const live = (state.profiles || []).find((p) => p.id === child.id) || child;
  const siblings = sameBandSiblings(state, live.id);
  const suggested = defaultSeedSourceFor(state, live.id);
  const suggestedSibling = suggested.kind === "sibling" ? (state.profiles || []).find((p) => p.id === suggested.siblingId) : null;
  const source = live._seedSource || null;
  const sourceSibling = source && source.kind === "sibling" ? (state.profiles || []).find((p) => p.id === source.siblingId) : null;
  const leGuide = LE_GUIDE_STEPS[Math.min(Math.max(lePart, 0), LE_GUIDE_STEPS.length - 1)];
  const leIsLastPart = lePart >= LE_GUIDE_STEPS.length - 1;
  // What the fork will seed from — chosen on the fork itself when a same-band sibling
  // exists ("Base it on: Alex's setup / Standard set"), defaulting to the sibling. The
  // review's provenance line can still switch it afterwards. Alan, 4 Sep: "there is no
  // exit ramp to start fresh" — the custom route never showed the review, so the standard
  // set was unreachable on it. Now the choice is made before either route.
  const [sourceChoice, setSourceChoice] = useState(() => defaultSeedSourceFor(state, child.id));
  const sourceFor = (s) => (sourceChoice.kind === "sibling" && (s.profiles || []).some((p) => p.id === sourceChoice.siblingId) ? sourceChoice : { kind: "standard" });
  const reseedFromSource = (s, id) => seedChildFromSource(s, id, (s.profiles.find((p) => p.id === id) || {})._seedSource || sourceFor(s));

  // Cap stage: presets, default = the same-band sibling's cap (or the household default).
  const siblingCap = suggestedSibling ? (suggestedSibling.spendingCap?.weeklyGBP ?? DEFAULT_SPENDING_CAP_GBP) : null;
  const [capChoice, setCapChoice] = useState(() => live.spendingCap?.weeklyGBP ?? siblingCap ?? DEFAULT_SPENDING_CAP_GBP);
  const [capCustomOpen, setCapCustomOpen] = useState(() => !CAP_PRESETS.includes(live.spendingCap?.weeklyGBP ?? siblingCap ?? DEFAULT_SPENDING_CAP_GBP));
  function commitCapStage() {
    const gbp = Math.round((parseFloat(capChoice) || DEFAULT_SPENDING_CAP_GBP) * 100) / 100;
    update((s) => {
      const c = s.profiles.find((p) => p.id === live.id);
      if (!c) return null;
      c.spendingCap = { ...(c.spendingCap || {}), enabled: true, weeklyGBP: gbp };
      if (s.settings.capMode !== "perChild" && (s.profiles || []).length > 1) {
        const others = s.profiles.filter((p) => p.id !== live.id);
        if (others.some((p) => (p.spendingCap?.weeklyGBP ?? DEFAULT_SPENDING_CAP_GBP) !== gbp)) s.settings.capMode = "perChild";
      }
      return s;
    });
    setStage("fork");
  }

  // Scroll to the sheet's own header (the child's name) on each stage — not the page top,
  // so the parent keeps their bearing. Alan, 4 Sep: "use the child switch as the anchor".
  const anchorRef = useRef(null);
  useEffect(() => {
    try { (anchorRef.current || window).scrollIntoView ? anchorRef.current.scrollIntoView({ block: "start", behavior: "auto" }) : window.scrollTo({ top: 0, behavior: "auto" }); } catch {}
  }, [stage, lePart]);

  function choose(route) {
    update((s) => { seedChildFromSource(s, live.id, sourceFor(s)); return s; });
    track("setup_fork_choice", { kind: route, via: "add_child" });
    setFromReview(false);
    setStage(route === "auto" ? "review" : "habits");
  }
  function pickSource(src) {
    setSourceChoice(src);
    update((s) => { seedChildFromSource(s, live.id, src); return s; });
    setPickingSource(false);
  }
  function finish() {
    if (showToast) showToast(ct("toast.child_set_up", "{name} is set up", { name: live.name }), "good");
    onDone();
  }
  function openFromReview(target) {
    setFromReview(true);
    if (target === "learnearn") { setLePart(0); setStage("le"); return; }
    setStep4Open({ habits: target === "habits", activities: target === "tasks" });
    setStage(target);
  }
  function leaveHabits() { if (fromReview) { setStage("review"); return; } setStep4Open({ habits: false, activities: true }); setStage("tasks"); }
  function leaveTasks() { if (fromReview) { setStage("review"); return; } setLePart(0); setStage("le"); }
  function leaveLe() { if (fromReview) { setStage("review"); return; } finish(); }

  const bandLabel = isYoungChild(live)
    ? ct("wizard.addchild.standard_young", "Standard set for under 8s")
    : ct("wizard.addchild.standard_older", "Standard set for ages 8 and over");
  const provenance = (
    <div className="border-t border-b border-slate-200 py-2.5 flex items-center gap-2" data-testid="review-provenance">
      <Icon id="ic-kids" size={16} className="flex-shrink-0" />
      <div className="text-xs font-bold text-emerald-800 flex-1 min-w-0">
        {sourceSibling
          ? ct("wizard.addchild.based_on", "Based on {name}'s setup", { name: sourceSibling.name })
          : bandLabel}
        {source && source.kind === "sibling" && source.dropped > 0 && (
          <span className="block font-semibold text-emerald-700/80" data-testid="review-provenance-dropped">
            {ct("wizard.addchild.dropped", "We left out {n} of {name}'s smaller tasks so it fits {cap} a week.", { n: source.dropped, name: sourceSibling?.name || "", cap: fmtPence(spendingCapPence(live) ?? 0) })}
          </span>
        )}
      </div>
      {(siblings.length > 0) && (
        <button type="button" className="text-xs font-extrabold text-emerald-700 flex items-center gap-1 flex-shrink-0" data-testid="review-change-source" onClick={() => setPickingSource(true)}>
          <Icon id="ic-edit" size={12} />{ct("wizard.review.change", "Change")}
        </button>
      )}
    </div>
  );

  const backLabel = ct("wizard.review.back_to_review", "Back to {name}'s setup", { name: live.name });
  const footerPrimary = (label, onClick, testId) => (
    <button className="flex-1 md:flex-none md:min-w-[180px] md:px-5 bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5" data-testid={testId} onClick={onClick}>{label}</button>
  );

  return (
    <div className="fixed inset-0 z-40 bg-slate-100 overflow-y-auto" data-testid="new-child-setup">
      {/* Same grid and width as the setup wizard (pass V kept the wizard two-column on wide
          screens). `max-w-lg` used to cap this sheet at 512px inside that grid, and the cap
          and fork stages had no left-column intro, so on a laptop the sheet rendered as a
          narrow right column beside an empty left one (Alan, staging 8 Sep). The header
          spans both columns; every stage now has an intro for column 1. */}
      <div className="p-3 sm:p-5 lb-form wizard-grid">
        {/* One full-width block in the grid's first row (the slot the wizard's progress bar
            takes), so the stage intro's explicit `grid-row: 2` lands beneath it, not on it. */}
        <div className="wizard-progress">
          <SafeAreaSpacer />
          <div ref={anchorRef} className="px-0.5 py-2 flex items-center gap-3" style={{ scrollMarginTop: "8px" }}>
            <ChildMark profile={live} size="md" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-500">{ct("wizard.addchild.kicker", "New child")}</div>
              <div className="text-base font-extrabold text-slate-900 truncate">{ct("wizard.addchild.title", "Set up {name}", { name: live.name })}</div>
            </div>
          </div>
        </div>

        {(stage === "cap" || stage === "fork") && (
          <WizardIntro
            icon={<Icon id="ic-cap" size={30} />}
            title={stage === "cap" ? ct("wizard.addchild.cap_intro_title", "Weekly cap") : ct("wizard.addchild.fork_intro_title", "Their starting set")}
            body={stage === "cap"
              ? ct("wizard.addchild.cap_intro_body", "One number sets the scale for everything else: tasks, the streak reward and the Learn & Earn games all size themselves to it.")
              : ct("wizard.addchild.fork_intro_body", "Everything can start from a brother or sister's setup or the standard set for their age. We can put it together for you, or walk you through each part.")}
          />
        )}

        {stage === "cap" && (
          <>
            <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="addchild-cap">
              <div className="flex items-center gap-2.5 mb-1">
                <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0"><Icon id="ic-cap" size={20} /></div>
                <div className="text-base font-extrabold text-slate-900">{ct("wizard.addchild.cap_heading", "How much can {name} earn a week?", { name: live.name })}</div>
              </div>
              <p className="text-sm text-slate-500 leading-snug mb-3">
                {siblingCap !== null
                  ? ct("wizard.addchild.cap_body_sibling", "Every reward amount is worked out from this. {sibling} is on {cap} a week — we've started {name} there.", { sibling: suggestedSibling.name, cap: fmtPence(Math.round(siblingCap * 100)), name: live.name })
                  : ct("wizard.addchild.cap_body", "Every reward amount is worked out from this — tasks, streaks and Learn & Earn all size themselves to it. You can change it any time.")}
              </p>
              <div className="flex flex-wrap gap-2">
                {CAP_PRESETS.map((v) => (
                  <button key={v} type="button" data-testid="cap-preset"
                    onClick={() => { setCapCustomOpen(false); setCapChoice(v); }}
                    className={`text-sm font-bold rounded-xl px-3.5 py-2 border ${!capCustomOpen && Number(capChoice) === v ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-500"}`}>
                    {CURRENCY_SYMBOL}{v}
                  </button>
                ))}
                <button type="button" onClick={() => setCapCustomOpen(true)}
                  className={`text-sm font-bold rounded-xl px-3.5 py-2 border ${capCustomOpen ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-500"}`}>
                  {ct("wizard.tokens.cap_other", "Other")}
                </button>
              </div>
              {capCustomOpen && (
                <div className="flex items-center gap-2 mt-2.5">
                  <label className="text-xs font-medium text-slate-500">{CURRENCY_SYMBOL} {ct("wizard.tokens.cap_per_week", "per week")}</label>
                  <input className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm w-24 text-right font-bold" type="number" min="0.01" step="any"
                    data-testid="cap-custom-input" value={capChoice} onChange={(e) => setCapChoice(e.target.value)} />
                </div>
              )}
            </div>
            <WizardFooter>
              <WizardBackButton onBack={() => { if (showToast) showToast(ct("toast.child_set_up_later", "You can set {name} up any time from Kids", { name: live.name }), "info"); onDone(); }} />
              <button className="flex-1 md:flex-none md:min-w-[180px] md:px-5 bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5" data-testid="addchild-cap-continue" onClick={commitCapStage} disabled={!(parseFloat(capChoice) > 0)}>
                {ct("wizard.common.continue", "Continue")}
              </button>
            </WizardFooter>
          </>
        )}

        {stage === "fork" && (
          <>
            {siblings.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 p-3.5" data-testid="addchild-source">
                <div className="text-sm font-bold text-slate-900 mb-0.5">{ct("wizard.addchild.source_heading", "Start {name} from", { name: live.name })}</div>
                <p className="text-xs text-slate-500 leading-snug mb-2.5">{ct("wizard.addchild.source_body", "A copy of a brother or sister's setup, sized to {name}'s cap — or the standard set for their age.", { name: live.name })}</p>
                <div className="flex flex-wrap gap-2">
                  {siblings.map((p) => (
                    <button key={p.id} type="button" data-testid="source-chip-sibling"
                      onClick={() => setSourceChoice({ kind: "sibling", siblingId: p.id })}
                      className={`text-sm font-bold rounded-xl px-3.5 py-2 border ${sourceChoice.kind === "sibling" && sourceChoice.siblingId === p.id ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-500"}`}>
                      {ct("wizard.addchild.pick_sibling", "{name}'s setup", { name: p.name })}
                    </button>
                  ))}
                  <button type="button" data-testid="source-chip-standard"
                    onClick={() => setSourceChoice({ kind: "standard" })}
                    className={`text-sm font-bold rounded-xl px-3.5 py-2 border ${sourceChoice.kind === "standard" ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-500"}`}>
                    {bandLabel}
                  </button>
                </div>
              </div>
            )}
            <SetupFork
              childCount={1}
              heading={ct("wizard.addchild.fork_heading", "How would you like to set {name} up?", { name: live.name })}
              body={sourceChoice.kind === "sibling" && (state.profiles || []).find((p) => p.id === sourceChoice.siblingId)
                ? ct("wizard.addchild.fork_body_sibling", "We'll base {name}'s daily habits, earning tasks and Learn & Earn games on {sibling}'s, sized to {name}'s own weekly cap — you'll see exactly what we chose and can change any of it.", { name: live.name, sibling: (state.profiles || []).find((p) => p.id === sourceChoice.siblingId).name })
                : ct("wizard.addchild.fork_body_standard", "We'll set up {name}'s daily habits, earning tasks and Learn & Earn games for their age, sized to their weekly cap — you'll see exactly what we chose and can change any of it.", { name: live.name })}
              onChoose={choose}
              onBack={() => setStage("cap")}
            />
          </>
        )}

        {stage === "review" && (
          <>
            <SetupReviewStep
              child={live}
              state={state}
              update={update}
              showToast={showToast}
              index={0}
              total={1}
              nextChild={null}
              hideContext
              provenance={provenance}
              reseed={reseedFromSource}
              continueLabel={ct("wizard.addchild.done", "Looks good, done")}
              onChange={openFromReview}
              onContinue={finish}
              onBack={() => setStage("fork")}
            />
            {pickingSource && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="source-picker">
                <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-5">
                  <div className="text-base font-extrabold text-slate-900 mb-1">{ct("wizard.addchild.pick_heading", "Base {name}'s setup on…", { name: live.name })}</div>
                  <p className="text-xs text-slate-500 mb-3 leading-snug">{ct("wizard.addchild.pick_body", "Anything you've already changed for {name} stays as it is.", { name: live.name })}</p>
                  <div className="flex flex-col gap-2">
                    {siblings.map((p) => (
                      <button key={p.id} type="button" className="w-full bg-white border border-slate-200 text-slate-700 font-bold text-sm rounded-xl py-2.5" data-testid="source-sibling" onClick={() => pickSource({ kind: "sibling", siblingId: p.id })}>
                        {ct("wizard.addchild.pick_sibling", "{name}'s setup", { name: p.name })}
                      </button>
                    ))}
                    <button type="button" className="w-full bg-white border border-slate-200 text-slate-700 font-bold text-sm rounded-xl py-2.5" data-testid="source-standard" onClick={() => pickSource({ kind: "standard" })}>
                      {bandLabel}
                    </button>
                    <button type="button" className="w-full text-slate-400 font-bold text-sm rounded-xl py-2" onClick={() => setPickingSource(false)}>
                      {ct("wizard.addchild.pick_cancel", "Keep it as it is")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {(stage === "habits" || stage === "tasks") && (
          <>
            <WizardIntro
              icon={<CoinIcon className="w-9 h-9" />}
              title={ct("wizard.habits.title", "Daily habits and earning activities")}
              body={stage === "habits"
                ? ct("wizard.habits.body1", "Start with the habit you want front and centre every day — how many tasks earn the weekly streak reward, and how much it pays. You can always add more later.")
                : ct("wizard.habits.body2", "Now the tasks they can do any time to earn tokens — reading, tidying, homework, whatever fits your household. We've priced these to fit their weekly cap; change anything you like.")}
            />
            <div className="flex flex-col gap-2">
              <DailyHabitsSection child={live} state={state} update={update} setupMode open={step4Open.habits} onToggle={(v) => setStep4Open((m) => ({ ...m, habits: v }))} compactClosed />
              {stage === "tasks" && <ChildTasksSection child={live} state={state} update={update} />}
              <EarningsHealthLine child={live} state={state} update={update} showToast={showToast} />
            </div>
            <WizardFooter>
              <WizardBackButton onBack={fromReview ? () => setStage("review") : stage === "habits" ? () => setStage("fork") : () => { setStep4Open({ habits: true, activities: false }); setStage("habits"); }} />
              {!fromReview && (
                <button className="flex-1 bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl py-2.5" onClick={stage === "habits" ? leaveHabits : leaveTasks}>{ct("wizard.common.skip_for_now", "Skip for now")}</button>
              )}
              {footerPrimary(
                fromReview ? backLabel : stage === "habits" ? ct("wizard.common.continue", "Continue") : ct("wizard.earn.next_learnearn", "Next: {name}'s Learn & Earn", { name: live.name }),
                stage === "habits" ? leaveHabits : leaveTasks,
                "wizard-step4-continue",
              )}
            </WizardFooter>
          </>
        )}

        {stage === "le" && (
          <>
            {lePart === 0 && (
              <WizardIntro icon={<Icon id="ic-brain" size={30} />} title={ct("wizard.learnearn.title", "Learn & Earn games")} body={ct("wizard.learnearn.body", "This is already set up. We've picked spelling, vocabulary and maths practice to suit {name}'s age, with a weekly reward limit on each subject — so there's nothing you have to do here.", { name: live.name })} />
            )}
            <LearningAdminTab state={state} update={update} activeChild={live} showToast={showToast} guideStep={leGuide.id} />
            <EarningsHealthLine child={live} state={state} update={update} showToast={showToast} />
            <WizardFooter>
              <WizardBackButton onBack={lePart > 0 ? () => setLePart((p) => p - 1) : fromReview ? () => setStage("review") : () => setStage("tasks")} />
              <button className="flex-1 bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl py-2.5" data-testid="wizard-step5-skip" onClick={leaveLe}>
                {fromReview ? ct("wizard.review.back_to_review_short", "Back to setup") : ct("wizard.common.skip_for_now", "Skip for now")}
              </button>
              {footerPrimary(
                !leIsLastPart ? ct("wizard.common.continue", "Continue") : fromReview ? backLabel : ct("wizard.addchild.done", "Looks good, done"),
                () => (leIsLastPart ? leaveLe() : setLePart((p) => p + 1)),
                "wizard-step5-continue",
              )}
            </WizardFooter>
          </>
        )}
        <SafeAreaSpacer />
      </div>
    </div>
  );
}
